import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Car, Footprints, Navigation, Loader2, MapPin, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const userIcon = L.divIcon({
  className: "",
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid white;box-shadow:0 0 0 2px #2563eb"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const Fit = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length) map.fitBounds(bounds, { padding: [45, 45], maxZoom: 16 });
  }, [JSON.stringify(bounds)]); // eslint-disable-line
  return null;
};

const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
const fmtDur = (s) => {
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}min`;
};

// locations: [{address, lat, lng}]
export const RouteMap = ({ locations = [], name = "" }) => {
  const { t } = useLang();
  const valid = locations.filter((l) => l && l.lat != null && l.lng != null);
  const dest = valid[0];
  const [userPos, setUserPos] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|locating|ok|denied
  const [mode, setMode] = useState("driving"); // driving|foot
  const [route, setRoute] = useState(null); // {coords, distance, duration}

  const locate = () => {
    if (!navigator.geolocation) { setStatus("denied"); return; }
    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserPos([pos.coords.latitude, pos.coords.longitude]); setStatus("ok"); },
      () => setStatus("denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => { if (dest) locate(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!userPos || !dest) return;
    const url = `https://router.project-osrm.org/route/v1/driving/${userPos[1]},${userPos[0]};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;
    let cancelled = false;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d.routes?.length) return;
        const r = d.routes[0];
        const coords = r.geometry.coordinates.map((c) => [c[1], c[0]]);
        // OSRM demo only serves car speeds; derive walking time from distance (~5 km/h)
        const duration = mode === "foot" ? r.distance / 1.389 : r.duration;
        setRoute({ coords, distance: r.distance, duration });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userPos, mode, dest?.lat, dest?.lng]);

  if (!dest) {
    return (
      <div className="h-64 rounded-2xl bg-muted grid place-items-center text-muted-foreground text-sm" data-testid="activity-map-container">
        {t("detail.location")}
      </div>
    );
  }

  const bizPoints = valid.map((l) => [l.lat, l.lng]);
  const bounds = userPos ? [userPos, ...bizPoints] : bizPoints;
  const gmapMode = mode === "foot" ? "walking" : "driving";
  const gmapUrl = userPos
    ? `https://www.google.com/maps/dir/?api=1&origin=${userPos[0]},${userPos[1]}&destination=${dest.lat},${dest.lng}&travelmode=${gmapMode}`
    : `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=${gmapMode}`;

  return (
    <div className="space-y-2" data-testid="activity-map-container">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-full bg-muted p-0.5" data-testid="route-mode-toggle">
          <button
            onClick={() => setMode("driving")}
            data-testid="route-mode-driving"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "driving" ? "bg-primary text-white" : "text-muted-foreground"}`}
          >
            <Car size={15} /> {t("route.car")}
          </button>
          <button
            onClick={() => setMode("foot")}
            data-testid="route-mode-foot"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "foot" ? "bg-primary text-white" : "text-muted-foreground"}`}
          >
            <Footprints size={15} /> {t("route.foot")}
          </button>
        </div>
        {route && status === "ok" && (
          <div className="flex items-center gap-2 text-sm font-semibold" data-testid="route-info">
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary"><Navigation size={14} /> {fmtDist(route.distance)}</span>
            <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary">{fmtDur(route.duration)}</span>
          </div>
        )}
      </div>

      {status === "locating" && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> {t("route.locating")}</p>
      )}
      {status === "denied" && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          {t("route.denied")}
          <button onClick={locate} className="text-primary font-semibold underline" data-testid="route-retry">{t("route.retry")}</button>
        </p>
      )}

      <div className="rounded-2xl overflow-hidden border border-border">
        <MapContainer center={bizPoints[0]} zoom={14} scrollWheelZoom={false} style={{ height: "18rem", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {valid.map((l, i) => (
            <Marker key={i} position={[l.lat, l.lng]}>
              <Popup><strong>{name}</strong>{l.address ? <><br />{l.address}</> : null}</Popup>
            </Marker>
          ))}
          {userPos && <Marker position={userPos} icon={userIcon}><Popup>{t("route.you")}</Popup></Marker>}
          {route && <Polyline positions={route.coords} pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.8 }} />}
          <Fit bounds={bounds} />
        </MapContainer>
      </div>

      <a
        href={gmapUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="route-open-google"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
      >
        <ExternalLink size={14} /> {t("route.openGoogle")}
      </a>
    </div>
  );
};
